import React, { useState, useRef, useEffect } from 'react';
import { ShoppingBag, User, Heart, Search, Menu, X, ArrowRight, ChevronRight, Plus, Instagram, Play, Star, Scissors, Ruler, Award, Clock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const Template17 = () => {
    const navigate = useNavigate();
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [hoveredProduct, setHoveredProduct] = useState(null);
    const [activeTestimonial, setActiveTestimonial] = useState(0);
    const [wishlist, setWishlist] = useState({});
    const touchStartX = useRef(0);

    // --- ORIGINAL data (desktop) ---
    const collections = [
        { name: 'New Season', image: 'https://images.unsplash.com/photo-1515886657613-9f3515b0c78f?w=800&h=1000&fit=crop' },
        { name: 'Best Sellers', image: 'https://images.unsplash.com/photo-1496747611176-843222e1e57c?w=800&h=1000&fit=crop' },
    ];

    const lookbook = [
        { image: 'https://images.unsplash.com/photo-1509631179647-0177331693ae?w=600&h=750&fit=crop', title: 'The Edit' },
        { image: 'https://images.unsplash.com/photo-1485968579580-b6d095142e6e?w=600&h=750&fit=crop', title: 'Essentials' },
        { image: 'https://images.unsplash.com/photo-1495385794356-15371f348c31?w=600&h=750&fit=crop', title: 'Evening' },
        { image: 'https://images.unsplash.com/photo-1558171813-4c088753af8f?w=600&h=750&fit=crop', title: 'Casual' },
    ];

    const desktopProducts = [
        { name: 'Draped Silk Blouse', price: 3200, color: 'Ivory', image: 'https://images.unsplash.com/photo-1515886657613-9f3515b0c78f?w=500&h=700&fit=crop', hoverImage: 'https://images.unsplash.com/photo-1496747611176-843222e1e57c?w=500&h=700&fit=crop', tag: 'New' },
        { name: 'Tailored Linen Trousers', price: 4500, color: 'Sand', image: 'https://images.unsplash.com/photo-1509631179647-0177331693ae?w=500&h=700&fit=crop', hoverImage: 'https://images.unsplash.com/photo-1485968579580-b6d095142e6e?w=500&h=700&fit=crop', tag: null },
        { name: 'Floral Midi Dress', price: 5800, color: 'Blush', image: 'https://images.unsplash.com/photo-1495385794356-15371f348c31?w=500&h=700&fit=crop', hoverImage: 'https://images.unsplash.com/photo-1558171813-4c088753af8f?w=500&h=700&fit=crop', tag: 'Bestseller' },
        { name: 'Cotton Kurta Set', price: 3800, color: 'Sage', image: 'https://images.unsplash.com/photo-1583391733956-3750e0ff4e8b?w=500&h=700&fit=crop', hoverImage: 'https://images.unsplash.com/photo-1610030469983-98e550d6193c?w=500&h=700&fit=crop', tag: null },
        { name: 'Relaxed Blazer', price: 6200, color: 'Charcoal', image: 'https://images.unsplash.com/photo-1594938298603-c8148c4dae35?w=500&h=700&fit=crop', hoverImage: 'https://images.unsplash.com/photo-1506629082955-511b1aa562c8?w=500&h=700&fit=crop', tag: 'New' },
        { name: 'Pleated Midi Skirt', price: 2900, color: 'Navy', image: 'https://images.unsplash.com/photo-1564257631407-4deb1f99d992?w=500&h=700&fit=crop', hoverImage: 'https://images.unsplash.com/photo-1515372039744-b8f02a3ae446?w=500&h=700&fit=crop', tag: null },
        { name: 'Oversized Shirt Dress', price: 4200, color: 'White', image: 'https://images.unsplash.com/photo-1594040226829-7f251ab46d80?w=500&h=700&fit=crop', hoverImage: 'https://images.unsplash.com/photo-1595950653106-6c9ebd614d3a?w=500&h=700&fit=crop', tag: null },
        { name: 'Embroidered Top', price: 2600, color: 'Cream', image: 'https://images.unsplash.com/photo-1610030469983-98e550d6193c?w=500&h=700&fit=crop', hoverImage: 'https://images.unsplash.com/photo-1583391733956-3750e0ff4e8b?w=500&h=700&fit=crop', tag: 'Limited' },
    ];

    const desktopInstagramPosts = [
        'https://images.unsplash.com/photo-1515886657613-9f3515b0c78f?w=400&h=400&fit=crop',
        'https://images.unsplash.com/photo-1496747611176-843222e1e57c?w=400&h=400&fit=crop',
        'https://images.unsplash.com/photo-1509631179647-0177331693ae?w=400&h=400&fit=crop',
        'https://images.unsplash.com/photo-1485968579580-b6d095142e6e?w=400&h=400&fit=crop',
        'https://images.unsplash.com/photo-1495385794356-15371f348c31?w=400&h=400&fit=crop',
        'https://images.unsplash.com/photo-1558171813-4c088753af8f?w=400&h=400&fit=crop',
    ];

    // --- CURRENT data (mobile) ---
    const categories = [
        { name: 'Bespoke Suits', image: 'https://images.unsplash.com/photo-1507679799987-c73779587ccf?w=600&h=800&fit=crop', count: '24 Pieces' },
        { name: 'Evening Gowns', image: 'https://images.unsplash.com/photo-1518622358385-8ea7d0794bf6?w=600&h=800&fit=crop', count: '18 Pieces' },
        { name: 'Bridal Couture', image: 'https://images.unsplash.com/photo-1594552072238-b8a33785b261?w=600&h=800&fit=crop', count: '12 Pieces' },
        { name: 'Tailored Shirts', image: 'https://images.unsplash.com/photo-1602810318383-e386cc2a3ccf?w=600&h=800&fit=crop', count: '32 Pieces' },
    ];

    const mobileProducts = [
        { name: 'Hand-Draped Silk Blouse', price: 12200, image: 'https://images.unsplash.com/photo-1595777457583-95e059d581b8?w=500&h=650&fit=crop', tag: 'Made to Order', color: 'Ivory' },
        { name: 'Tailored Wool Trousers', price: 18500, image: 'https://images.unsplash.com/photo-1584917865442-de89df76afd3?w=500&h=650&fit=crop', tag: null, color: 'Charcoal' },
        { name: 'Embroidered Sherwani', price: 34800, image: 'https://images.unsplash.com/photo-1543163521-1bf539c55dd2?w=500&h=650&fit=crop', tag: 'Atelier Exclusive', color: 'Gold' },
        { name: 'Structured Linen Blazer', price: 22600, image: 'https://images.unsplash.com/photo-1591047139829-d91aecb6caea?w=500&h=650&fit=crop', tag: null, color: 'Sand' },
        { name: 'Pleated Midi Skirt', price: 14900, image: 'https://images.unsplash.com/photo-1564257631407-4deb1f99d992?w=500&h=650&fit=crop', tag: 'New', color: 'Blush' },
        { name: 'Bespoke Kurta Set', price: 19800, image: 'https://images.unsplash.com/photo-1521572163474-6864f9cf17ab?w=500&h=650&fit=crop', tag: null, color: 'Sage' },
    ];

    const testimonials = [
        { text: 'Every stitch tells a story. The attention to detail in my wedding lehenga was beyond anything I imagined. Truly bespoke craftsmanship.', name: 'Aditi Mehta', role: 'Bride, Mumbai', rating: 5 },
        { text: 'I have been a patron for three years now. Each piece is measured, cut, and finished with an artisan\'s eye. Nothing compares.', name: 'Raghav Singhania', role: 'Collector, Delhi', rating: 5 },
        { text: 'The consultation process itself felt luxurious. They understood my vision and translated it into fabric poetry.', name: 'Nandini Rao', role: 'Patron, Bangalore', rating: 5 },
    ];

    const features = [
        { icon: Scissors, title: 'Hand-Cut Patterns', desc: 'Every piece individually drafted' },
        { icon: Ruler, title: 'Perfect Fit', desc: '27-point measurement system' },
        { icon: Award, title: 'Heritage Craft', desc: 'Third-generation artisans' },
        { icon: Clock, title: 'Made to Order', desc: '3-6 weeks delivery' },
    ];

    const mobileInstagramPosts = [
        'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=400&h=400&fit=crop',
        'https://images.unsplash.com/photo-1529139574466-a303027c1d8b?w=400&h=400&fit=crop',
        'https://images.unsplash.com/photo-1469334031218-e382a71b716b?w=400&h=400&fit=crop',
        'https://images.unsplash.com/photo-1487412720507-e7ab37603c6f?w=400&h=400&fit=crop',
        'https://images.unsplash.com/photo-1524504388940-b1c1722653e1?w=400&h=400&fit=crop',
        'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=400&h=400&fit=crop',
    ];

    useEffect(() => {
        const timer = setInterval(() => {
            setActiveTestimonial(prev => (prev + 1) % testimonials.length);
        }, 5000);
        return () => clearInterval(timer);
    }, [testimonials.length]);

    const handleTestimonialSwipe = (e, type) => {
        if (type === 'start') touchStartX.current = e.touches[0].clientX;
        if (type === 'end') {
            const diff = touchStartX.current - e.changedTouches[0].clientX;
            if (Math.abs(diff) > 50) {
                setActiveTestimonial(prev => diff > 0 ? (prev + 1) % testimonials.length : (prev - 1 + testimonials.length) % testimonials.length);
            }
        }
    };

    return (
        <div className="w-full min-h-screen bg-white font-sans">
            {/* ==================== TOP BAR ==================== */}
            {/* Mobile Top Bar */}
            <div className="lg:hidden bg-neutral-900 text-white text-center py-2 text-[10px] sm:text-xs tracking-[0.25em] px-4">
                COMPLIMENTARY CONSULTATIONS | BESPOKE TAILORING SINCE 1987
            </div>
            {/* Desktop Top Bar */}
            <div className="hidden lg:block bg-neutral-900 text-white text-center py-2 text-xs tracking-widest">
                FREE SHIPPING ON ORDERS OVER ₹2,000 | EASY RETURNS
            </div>

            {/* ==================== HEADER ==================== */}
            {/* Mobile Header */}
            <header className="lg:hidden bg-white sticky top-0 z-50 border-b border-neutral-100">
                <div className="max-w-screen-2xl mx-auto">
                    <div className="flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4">
                        <button className="min-w-[44px] min-h-[44px] flex items-center justify-center" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
                            {mobileMenuOpen ? <X className="w-5 h-5 text-neutral-800" /> : <Menu className="w-5 h-5 text-neutral-800" />}
                        </button>
                        <h1 onClick={() => navigate('/template17')} className="text-xl sm:text-2xl font-light tracking-[0.2em] text-neutral-900 cursor-pointer">MAISON</h1>
                        <div className="flex items-center gap-3 sm:gap-5">
                            <button className="min-w-[44px] min-h-[44px] flex items-center justify-center"><Search className="w-5 h-5 text-neutral-600 hover:text-neutral-900 transition-colors" /></button>
                            <button className="min-w-[44px] min-h-[44px] hidden sm:flex items-center justify-center" onClick={() => navigate('/template17/profile')}><User className="w-5 h-5 text-neutral-600 hover:text-neutral-900 transition-colors" /></button>
                            <button className="relative min-w-[44px] min-h-[44px] flex items-center justify-center" onClick={() => navigate('/template17/cart')}>
                                <ShoppingBag className="w-5 h-5 text-neutral-600 hover:text-neutral-900 transition-colors" />
                                <span className="absolute top-1 right-0.5 w-4 h-4 bg-neutral-900 text-white text-[10px] rounded-full flex items-center justify-center">0</span>
                            </button>
                        </div>
                    </div>
                </div>
                <div className={`bg-white border-t border-neutral-100 overflow-hidden transition-all duration-300 ${mobileMenuOpen ? 'max-h-[400px] opacity-100' : 'max-h-0 opacity-0'}`}>
                    <nav className="px-5 py-6 space-y-1">
                        <button onClick={() => { navigate('/template17/shop'); setMobileMenuOpen(false); }} className="block w-full text-left py-3 text-base text-neutral-800 border-b border-neutral-50 tracking-wide">Atelier</button>
                        <button onClick={() => setMobileMenuOpen(false)} className="block w-full text-left py-3 text-base text-neutral-800 border-b border-neutral-50 tracking-wide">Collections</button>
                        <button onClick={() => setMobileMenuOpen(false)} className="block w-full text-left py-3 text-base text-neutral-800 border-b border-neutral-50 tracking-wide">Bespoke</button>
                        <button onClick={() => { navigate('/template17/contact'); setMobileMenuOpen(false); }} className="block w-full text-left py-3 text-base text-neutral-800 border-b border-neutral-50 tracking-wide">Contact</button>
                        <div className="flex gap-4 pt-4">
                            <button onClick={() => { navigate('/template17/profile'); setMobileMenuOpen(false); }} className="flex items-center gap-2 text-sm text-neutral-500"><User className="w-4 h-4" /> Account</button>
                            <button className="flex items-center gap-2 text-sm text-neutral-500"><Heart className="w-4 h-4" /> Wishlist</button>
                        </div>
                    </nav>
                </div>
            </header>

            {/* Desktop Header - EXACT original */}
            <header className="hidden lg:block bg-white sticky top-0 z-50 border-b border-neutral-100">
                <div className="max-w-screen-2xl mx-auto">
                    <div className="flex items-center justify-between px-6 lg:px-12 py-4">
                        {/* Desktop Nav Left */}
                        <nav className="flex items-center gap-8">
                            <button onClick={() => navigate('/template17/shop')} className="text-sm text-neutral-700 hover:text-neutral-900 transition-colors tracking-wide">
                                Shop
                            </button>
                            <button className="text-sm text-neutral-700 hover:text-neutral-900 transition-colors tracking-wide">
                                Collections
                            </button>
                            <button className="text-sm text-neutral-700 hover:text-neutral-900 transition-colors tracking-wide">
                                Lookbook
                            </button>
                        </nav>

                        {/* Logo */}
                        <h1 onClick={() => navigate('/template17')} className="text-2xl lg:text-3xl font-semibold tracking-[0.15em] text-neutral-900 cursor-pointer">
                            BLUM
                        </h1>

                        {/* Icons Right */}
                        <div className="flex items-center gap-5">
                            <Search className="w-5 h-5 text-neutral-700 cursor-pointer hover:text-neutral-900 transition-colors" />
                            <User className="w-5 h-5 text-neutral-700 cursor-pointer hover:text-neutral-900 transition-colors" onClick={() => navigate('/template17/profile')} />
                            <Heart className="w-5 h-5 text-neutral-700 cursor-pointer hover:text-neutral-900 transition-colors" />
                            <div className="relative cursor-pointer" onClick={() => navigate('/template17/cart')}>
                                <ShoppingBag className="w-5 h-5 text-neutral-700 hover:text-neutral-900 transition-colors" />
                                <span className="absolute -top-1.5 -right-1.5 w-4 h-4 bg-neutral-900 text-white text-[10px] rounded-full flex items-center justify-center">0</span>
                            </div>
                        </div>
                    </div>
                </div>
            </header>

            {/* ==================== HERO ==================== */}
            {/* Mobile Hero - current design */}
            <section className="lg:hidden">
                <div className="relative aspect-[4/5]">
                    <img src="https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=1920&q=80" alt="Atelier Couture" className="w-full h-full object-cover" />
                </div>
                <div className="bg-neutral-50 px-5 py-8 text-center">
                    <p className="text-neutral-400 text-[10px] tracking-[0.4em] mb-2">AUTUMN/WINTER COUTURE</p>
                    <h2 className="text-2xl sm:text-3xl font-light text-neutral-900 mb-3 leading-tight tracking-wide">The Art of<br />Bespoke Tailoring</h2>
                    <p className="text-[14px] text-neutral-500 mb-6 max-w-sm mx-auto leading-relaxed">Each garment is individually crafted by master artisans using heritage techniques passed down through generations.</p>
                    <button onClick={() => navigate('/template17/shop')} className="w-full py-4 bg-neutral-900 text-white text-sm tracking-[0.2em] hover:bg-neutral-800 transition-colors min-h-[48px]">
                        BOOK A CONSULTATION
                    </button>
                </div>
            </section>

            {/* Desktop Hero - EXACT original split screen */}
            <section className="hidden lg:grid grid-cols-2 min-h-[90vh]">
                {collections.map((collection, idx) => (
                    <div key={idx} className="relative group cursor-pointer overflow-hidden">
                        <img
                            src={collection.image}
                            alt={collection.name}
                            className="w-full h-full min-h-[90vh] object-cover group-hover:scale-105 transition-transform duration-700"
                        />
                        <div className="absolute inset-0 bg-black/20 group-hover:bg-black/30 transition-colors duration-500"></div>
                        <div className="absolute inset-0 flex items-end p-12">
                            <div>
                                <h2 className="text-4xl font-light text-white mb-4">{collection.name}</h2>
                                <button className="group/btn flex items-center gap-2 text-white text-sm tracking-wider border-b border-white pb-1 hover:gap-3 transition-all">
                                    Shop Now <ArrowRight className="w-4 h-4" />
                                </button>
                            </div>
                        </div>
                    </div>
                ))}
            </section>

            {/* ==================== CATEGORIES (mobile) / PRODUCTS (desktop) ==================== */}
            {/* Mobile Categories - horizontal scroll */}
            <section className="lg:hidden py-10 sm:py-16">
                <div className="max-w-screen-2xl mx-auto">
                    <div className="px-5 sm:px-6 mb-6 sm:mb-10">
                        <p className="text-neutral-400 text-[10px] sm:text-xs tracking-[0.3em] mb-1">OUR SPECIALTIES</p>
                        <h2 className="text-xl sm:text-2xl font-light text-neutral-900 tracking-wide">Couture Collections</h2>
                    </div>
                    <div className="flex overflow-x-auto gap-3 px-5 snap-x snap-mandatory scrollbar-hide" style={{ WebkitOverflowScrolling: 'touch' }}>
                        {categories.map((cat, idx) => (
                            <div key={idx} className="w-[68vw] flex-shrink-0 snap-center group cursor-pointer">
                                <div className="relative aspect-[3/4] overflow-hidden rounded-2xl">
                                    <img src={cat.image} alt={cat.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                                    <div className="absolute inset-0 bg-gradient-to-t from-neutral-900/70 via-transparent to-transparent" />
                                    <div className="absolute bottom-0 left-0 right-0 p-5">
                                        <h3 className="text-lg sm:text-xl font-light text-white tracking-wide">{cat.name}</h3>
                                        <p className="text-neutral-300 text-xs mt-1">{cat.count}</p>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                    <div className="px-5 mt-2">
                        <div className="w-16 h-0.5 bg-neutral-200 rounded-full mx-auto"><div className="w-6 h-full bg-neutral-500 rounded-full" /></div>
                    </div>
                </div>
            </section>

            {/* ==================== PRODUCTS ==================== */}
            {/* Mobile Products - horizontal scroll with wishlist/quick-add */}
            <section className="lg:hidden py-10 sm:py-16 bg-neutral-50">
                <div className="max-w-screen-2xl mx-auto">
                    <div className="flex items-end justify-between px-5 sm:px-6 mb-6 sm:mb-10">
                        <div>
                            <p className="text-neutral-400 text-[10px] sm:text-xs tracking-[0.3em] mb-1">CURATED</p>
                            <h2 className="text-xl sm:text-2xl font-light text-neutral-900 tracking-wide">Atelier Pieces</h2>
                        </div>
                    </div>
                    <div className="flex overflow-x-auto gap-3 px-5 snap-x snap-mandatory scrollbar-hide" style={{ WebkitOverflowScrolling: 'touch' }}>
                        {mobileProducts.map((product, idx) => (
                            <div key={idx} className="w-[44vw] flex-shrink-0 snap-start group">
                                <div className="relative aspect-[3/4] overflow-hidden mb-2 sm:mb-3 bg-neutral-100">
                                    <img src={product.image} alt={product.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                                    {product.tag && <span className="absolute top-2 left-2 bg-neutral-900 text-white text-[9px] sm:text-[10px] tracking-wider px-2 py-1">{product.tag}</span>}
                                    <button onClick={() => setWishlist(prev => ({ ...prev, [idx]: !prev[idx] }))} className="absolute top-2 right-2 w-8 h-8 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center shadow-sm">
                                        <Heart className={`w-3.5 h-3.5 ${wishlist[idx] ? 'fill-red-500 text-red-500' : 'text-neutral-600'}`} />
                                    </button>
                                    <button className="absolute bottom-2 right-2 w-8 h-8 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center shadow-md sm:hidden">
                                        <Plus className="w-4 h-4 text-neutral-800" />
                                    </button>
                                </div>
                                <p className="text-[9px] sm:text-[10px] text-neutral-400 tracking-wider mb-0.5">{product.color.toUpperCase()}</p>
                                <h3 className="text-xs sm:text-sm text-neutral-900 mb-0.5 leading-tight">{product.name}</h3>
                                <p className="text-xs sm:text-sm text-neutral-600">{'\u20B9'}{product.price.toLocaleString()}</p>
                            </div>
                        ))}
                    </div>
                    <div className="text-center mt-6 px-5">
                        <button onClick={() => navigate('/template17/shop')} className="text-sm text-neutral-700 tracking-wider border-b border-neutral-400 pb-1 min-h-[44px] inline-flex items-center">View All Pieces</button>
                    </div>
                </div>
            </section>

            {/* Desktop Products - EXACT original grid with hover effects */}
            <section className="hidden lg:block py-28">
                <div className="max-w-screen-2xl mx-auto px-12">
                    <div className="flex items-end justify-between mb-12">
                        <div>
                            <p className="text-neutral-500 text-sm tracking-wider mb-2">CURATED FOR YOU</p>
                            <h2 className="text-4xl font-light text-neutral-900">New Arrivals</h2>
                        </div>
                        <button onClick={() => navigate('/template17/shop')} className="flex items-center gap-2 text-sm text-neutral-700 hover:text-neutral-900 transition-colors">
                            View All <ChevronRight className="w-4 h-4" />
                        </button>
                    </div>

                    <div className="grid grid-cols-4 gap-6">
                        {desktopProducts.map((product, idx) => (
                            <div
                                key={idx}
                                className="group"
                                onMouseEnter={() => setHoveredProduct(idx)}
                                onMouseLeave={() => setHoveredProduct(null)}
                            >
                                <div className="relative aspect-[3/4] overflow-hidden mb-4 bg-neutral-100">
                                    <img
                                        src={hoveredProduct === idx ? product.hoverImage : product.image}
                                        alt={product.name}
                                        className="w-full h-full object-cover transition-all duration-500"
                                    />
                                    {product.tag && (
                                        <span className="absolute top-3 left-3 bg-neutral-900 text-white text-xs px-3 py-1">{product.tag}</span>
                                    )}
                                    <button className="absolute top-3 right-3 w-9 h-9 bg-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300 shadow-sm">
                                        <Heart className="w-4 h-4 text-neutral-700" />
                                    </button>
                                    <button className="absolute bottom-4 left-4 right-4 py-3 bg-white text-neutral-900 text-xs tracking-widest opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all duration-300 flex items-center justify-center gap-2">
                                        <Plus className="w-3 h-3" /> ADD TO BAG
                                    </button>
                                </div>
                                <div>
                                    <p className="text-xs text-neutral-500 mb-1">{product.color}</p>
                                    <h3 className="text-sm text-neutral-900 mb-1">{product.name}</h3>
                                    <p className="text-sm text-neutral-700">₹{product.price.toLocaleString()}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ==================== LOOKBOOK (desktop only) ==================== */}
            <section className="hidden lg:block py-28 bg-neutral-50">
                <div className="max-w-screen-2xl mx-auto px-12">
                    <div className="text-center mb-14">
                        <p className="text-neutral-500 text-sm tracking-wider mb-2">SEASONAL EDIT</p>
                        <h2 className="text-4xl font-light text-neutral-900">The Lookbook</h2>
                    </div>

                    <div className="grid grid-cols-4 gap-4">
                        {lookbook.map((item, idx) => (
                            <div key={idx} className="group cursor-pointer">
                                <div className="relative aspect-[4/5] overflow-hidden">
                                    <img
                                        src={item.image}
                                        alt={item.title}
                                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                                    />
                                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-500"></div>
                                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                                        <span className="text-white text-lg tracking-wider">{item.title}</span>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="text-center mt-10">
                        <button className="text-sm text-neutral-800 tracking-wider border-b border-neutral-800 pb-1 hover:text-neutral-600 hover:border-neutral-600 transition-colors">
                            VIEW FULL LOOKBOOK
                        </button>
                    </div>
                </div>
            </section>

            {/* ==================== BRAND STORY ==================== */}
            {/* Mobile Brand Story - full-bleed stacked */}
            <section className="lg:hidden py-10 sm:py-16">
                <div>
                    <img src="https://images.unsplash.com/photo-1515886657613-9f3515b0c78f?w=1920&q=80" alt="Our Atelier" className="w-full aspect-[4/5] object-cover" />
                    <div className="px-5 py-8">
                        <p className="text-neutral-400 text-[10px] tracking-[0.3em] mb-2">OUR PHILOSOPHY</p>
                        <h2 className="text-xl font-light text-neutral-900 mb-4 leading-relaxed tracking-wide">Craftsmanship Woven Into Every Thread</h2>
                        <p className="text-[14px] text-neutral-500 leading-relaxed mb-4">At Maison, we believe clothing should be an extension of the wearer. Our atelier has served discerning patrons since 1987, combining old-world tailoring with contemporary design.</p>
                        <p className="text-[14px] text-neutral-500 leading-relaxed mb-6">Every garment begins with a personal consultation, progresses through hand-drafted patterns, and is finished with meticulous hand-stitching.</p>
                        <button className="text-sm text-neutral-900 tracking-[0.15em] border-b border-neutral-900 pb-1 min-h-[44px] inline-flex items-center">VISIT THE ATELIER</button>
                    </div>
                </div>
            </section>

            {/* Desktop Video Banner - EXACT original */}
            <section className="hidden lg:block relative h-[70vh]">
                <img
                    src="https://images.unsplash.com/photo-1509631179647-0177331693ae?w=1600&h=900&fit=crop"
                    alt="Video Banner"
                    className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-black/40"></div>
                <div className="absolute inset-0 flex items-center justify-center">
                    <div className="text-center">
                        <button className="w-20 h-20 bg-white rounded-full flex items-center justify-center mb-6 mx-auto hover:scale-110 transition-transform">
                            <Play className="w-8 h-8 text-neutral-800 ml-1" />
                        </button>
                        <p className="text-white/80 text-xs tracking-[0.3em] mb-3">WATCH THE FILM</p>
                        <h2 className="text-5xl font-light text-white">Behind The Collection</h2>
                    </div>
                </div>
            </section>

            {/* Desktop Story Section - EXACT original */}
            <section className="hidden lg:block py-28">
                <div className="max-w-screen-2xl mx-auto px-12">
                    <div className="grid grid-cols-2 gap-20 items-center">
                        <div>
                            <p className="text-neutral-500 text-sm tracking-wider mb-4">OUR PHILOSOPHY</p>
                            <h2 className="text-4xl font-light text-neutral-900 mb-6 leading-relaxed">
                                Thoughtfully Designed, Consciously Made
                            </h2>
                            <p className="text-neutral-600 leading-relaxed mb-6">
                                At Blum, we believe in creating clothing that transcends trends. Each piece is designed
                                with intention, crafted from quality materials, and made to become a lasting part of your wardrobe.
                            </p>
                            <p className="text-neutral-600 leading-relaxed mb-8">
                                We work with skilled artisans across India, combining traditional techniques with modern design
                                to create pieces that feel both timeless and contemporary.
                            </p>
                            <button className="text-sm text-neutral-900 tracking-wider border-b border-neutral-900 pb-1 hover:text-neutral-600 hover:border-neutral-600 transition-colors">
                                LEARN MORE
                            </button>
                        </div>
                        <div>
                            <img
                                src="https://images.unsplash.com/photo-1485968579580-b6d095142e6e?w=800&h=1000&fit=crop"
                                alt="Our Story"
                                className="w-full aspect-[4/5] object-cover"
                            />
                        </div>
                    </div>
                </div>
            </section>

            {/* ==================== TESTIMONIALS (mobile only) ==================== */}
            <section className="lg:hidden py-10 sm:py-16 bg-neutral-900">
                <div className="max-w-screen-xl mx-auto px-5 sm:px-6">
                    <div className="text-center mb-8">
                        <p className="text-neutral-500 text-[10px] sm:text-xs tracking-[0.3em] mb-2">PATRON VOICES</p>
                        <h2 className="text-xl sm:text-2xl font-light text-white tracking-wide">Words of Appreciation</h2>
                    </div>
                    <div className="overflow-hidden" onTouchStart={e => handleTestimonialSwipe(e, 'start')} onTouchEnd={e => handleTestimonialSwipe(e, 'end')}>
                        <div className="flex transition-transform duration-500 ease-out" style={{ transform: `translateX(-${activeTestimonial * 100}%)` }}>
                            {testimonials.map((t, idx) => (
                                <div key={idx} className="w-full flex-shrink-0 px-2">
                                    <div className="text-center">
                                        <div className="flex justify-center gap-1 mb-4">{[...Array(t.rating)].map((_, i) => <Star key={i} className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />)}</div>
                                        <p className="text-[14px] text-neutral-300 leading-relaxed mb-5 italic font-light">"{t.text}"</p>
                                        <p className="text-white tracking-wide text-sm">{t.name}</p>
                                        <p className="text-neutral-500 text-xs mt-1">{t.role}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div className="flex justify-center gap-2 mt-6">
                            {testimonials.map((_, idx) => (
                                <button key={idx} onClick={() => setActiveTestimonial(idx)} className={`w-2 h-2 rounded-full transition-all ${activeTestimonial === idx ? 'bg-white w-6' : 'bg-neutral-600'}`} />
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* ==================== FEATURES (mobile only) ==================== */}
            <section className="lg:hidden py-10 sm:py-16 bg-neutral-50">
                <div className="max-w-screen-2xl mx-auto">
                    <div className="px-5 sm:px-6 mb-6 text-center">
                        <p className="text-neutral-400 text-[10px] sm:text-xs tracking-[0.3em] mb-1">THE MAISON DIFFERENCE</p>
                        <h2 className="text-xl sm:text-2xl font-light text-neutral-900 tracking-wide">Our Commitment</h2>
                    </div>
                    <div className="flex overflow-x-auto gap-4 px-5 snap-x snap-mandatory scrollbar-hide" style={{ WebkitOverflowScrolling: 'touch' }}>
                        {features.map((f, idx) => (
                            <div key={idx} className="w-[60vw] flex-shrink-0 snap-center text-center p-6 bg-white">
                                <f.icon className="w-6 h-6 text-neutral-400 mx-auto mb-3" strokeWidth={1.5} />
                                <h3 className="text-sm sm:text-base font-medium text-neutral-900 mb-1 tracking-wide">{f.title}</h3>
                                <p className="text-[14px] text-neutral-500">{f.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ==================== INSTAGRAM ==================== */}
            {/* Mobile Instagram */}
            <section className="lg:hidden py-10 sm:py-16">
                <div className="max-w-screen-2xl mx-auto px-5 sm:px-6">
                    <div className="text-center mb-6">
                        <div className="flex items-center justify-center gap-2 mb-2">
                            <Instagram className="w-4 h-4 text-neutral-600" />
                            <span className="text-xs text-neutral-500 tracking-[0.2em]">@maison.atelier</span>
                        </div>
                        <h2 className="text-xl sm:text-2xl font-light text-neutral-900 tracking-wide">From the Atelier</h2>
                    </div>
                    <div className="grid grid-cols-3 gap-1 sm:gap-1.5">
                        {mobileInstagramPosts.map((post, idx) => (
                            <div key={idx} className="aspect-square group cursor-pointer overflow-hidden">
                                <img src={post} alt={`Atelier ${idx + 1}`} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Desktop Instagram - EXACT original */}
            <section className="hidden lg:block py-20 bg-neutral-50">
                <div className="max-w-screen-2xl mx-auto px-12">
                    <div className="text-center mb-10">
                        <div className="flex items-center justify-center gap-2 mb-4">
                            <Instagram className="w-5 h-5 text-neutral-700" />
                            <span className="text-sm text-neutral-700 tracking-wider">@blum.india</span>
                        </div>
                        <h2 className="text-3xl font-light text-neutral-900">Follow Our Journey</h2>
                    </div>

                    <div className="grid grid-cols-6 gap-2">
                        {desktopInstagramPosts.map((post, idx) => (
                            <div key={idx} className="aspect-square group cursor-pointer overflow-hidden">
                                <img
                                    src={post}
                                    alt={`Instagram ${idx + 1}`}
                                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                                />
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ==================== NEWSLETTER ==================== */}
            {/* Mobile Newsletter */}
            <section className="lg:hidden py-10 sm:py-16 bg-neutral-900 text-white">
                <div className="max-w-xl mx-auto px-5 sm:px-6 text-center">
                    <h2 className="text-xl sm:text-2xl font-light mb-3 tracking-wide">The Atelier Circle</h2>
                    <p className="text-neutral-400 text-[14px] sm:text-sm mb-6 sm:mb-8">Receive exclusive invitations to trunk shows, early access to new collections, and bespoke style guidance.</p>
                    <div className="flex flex-col sm:flex-row gap-3">
                        <input type="email" placeholder="Your email address" className="flex-1 px-5 py-4 bg-neutral-800 border-0 text-white text-sm placeholder-neutral-500 focus:outline-none focus:ring-1 focus:ring-neutral-600 rounded-lg sm:rounded-none min-h-[48px]" />
                        <button className="px-8 py-4 bg-white text-neutral-900 text-xs tracking-[0.2em] hover:bg-neutral-200 transition-colors w-full sm:w-auto rounded-lg sm:rounded-none min-h-[48px]">JOIN</button>
                    </div>
                    <p className="text-neutral-600 text-xs mt-4">By invitation and subscription. Unsubscribe anytime.</p>
                </div>
            </section>

            {/* Desktop Newsletter - EXACT original */}
            <section className="hidden lg:block py-24 bg-neutral-900 text-white">
                <div className="max-w-xl mx-auto px-6 text-center">
                    <h2 className="text-3xl font-light mb-4">Join the List</h2>
                    <p className="text-neutral-400 text-sm mb-8">
                        Subscribe for exclusive access to new collections, special offers, and style inspiration.
                    </p>
                    <div className="flex gap-3">
                        <input
                            type="email"
                            placeholder="Your email address"
                            className="flex-1 px-5 py-4 bg-neutral-800 border-0 text-white text-sm placeholder-neutral-500 focus:outline-none focus:ring-1 focus:ring-neutral-600"
                        />
                        <button className="px-8 py-4 bg-white text-neutral-900 text-xs tracking-widest hover:bg-neutral-200 transition-colors">
                            SUBSCRIBE
                        </button>
                    </div>
                    <p className="text-neutral-500 text-xs mt-4">No spam. Unsubscribe anytime.</p>
                </div>
            </section>

            {/* ==================== FOOTER ==================== */}
            {/* Mobile Footer */}
            <footer className="lg:hidden bg-white border-t border-neutral-200">
                <div className="max-w-screen-2xl mx-auto px-5 sm:px-6 py-10 sm:py-16">
                    <div className="grid grid-cols-2 gap-8">
                        <div className="col-span-2">
                            <h3 className="text-xl font-light tracking-[0.2em] mb-4">MAISON</h3>
                            <p className="text-neutral-500 text-sm leading-relaxed">Bespoke tailoring and couture craftsmanship since 1987. Made in India with world-class artistry.</p>
                        </div>
                        <div>
                            <h4 className="text-[10px] tracking-[0.2em] mb-4 text-neutral-400">ATELIER</h4>
                            <ul className="space-y-2 text-sm">
                                <li><span onClick={() => navigate('/template17/shop')} className="text-neutral-600 hover:text-neutral-900 cursor-pointer transition-colors min-h-[44px] inline-flex items-center">All Pieces</span></li>
                                <li><span className="text-neutral-600 hover:text-neutral-900 cursor-pointer transition-colors min-h-[44px] inline-flex items-center">New Arrivals</span></li>
                                <li><span className="text-neutral-600 hover:text-neutral-900 cursor-pointer transition-colors min-h-[44px] inline-flex items-center">Bespoke</span></li>
                            </ul>
                        </div>
                        <div>
                            <h4 className="text-[10px] tracking-[0.2em] mb-4 text-neutral-400">ASSISTANCE</h4>
                            <ul className="space-y-2 text-sm">
                                <li><span onClick={() => navigate('/template17/contact')} className="text-neutral-600 hover:text-neutral-900 cursor-pointer transition-colors min-h-[44px] inline-flex items-center">Contact</span></li>
                                <li><span onClick={() => navigate('/template17/orders')} className="text-neutral-600 hover:text-neutral-900 cursor-pointer transition-colors min-h-[44px] inline-flex items-center">Track Order</span></li>
                                <li><span className="text-neutral-600 hover:text-neutral-900 cursor-pointer transition-colors min-h-[44px] inline-flex items-center">Appointments</span></li>
                            </ul>
                        </div>
                        <div>
                            <h4 className="text-[10px] tracking-[0.2em] mb-4 text-neutral-400">CONNECT</h4>
                            <ul className="space-y-2 text-sm">
                                <li className="text-neutral-600">atelier@maison.in</li>
                                <li className="text-neutral-600">+91 98765 43210</li>
                                <li className="text-neutral-600 pt-1">By appointment only</li>
                            </ul>
                        </div>
                    </div>
                    <div className="border-t border-neutral-200 mt-8 pt-6 flex flex-col items-center gap-4">
                        <p className="text-neutral-400 text-xs">&copy; 2025 Maison. All rights reserved.</p>
                        <div className="flex gap-6 text-xs text-neutral-400">
                            <span className="hover:text-neutral-900 cursor-pointer transition-colors min-h-[44px] inline-flex items-center">Privacy</span>
                            <span className="hover:text-neutral-900 cursor-pointer transition-colors min-h-[44px] inline-flex items-center">Terms</span>
                        </div>
                    </div>
                </div>
            </footer>

            {/* Desktop Footer - EXACT original */}
            <footer className="hidden lg:block bg-white border-t border-neutral-200">
                <div className="max-w-screen-2xl mx-auto px-12 py-20">
                    <div className="grid grid-cols-4 gap-16">
                        <div>
                            <h3 className="text-xl font-semibold tracking-[0.1em] mb-6">BLUM</h3>
                            <p className="text-neutral-600 text-sm leading-relaxed">
                                Modern essentials for the contemporary wardrobe. Thoughtfully made in India.
                            </p>
                        </div>

                        <div>
                            <h4 className="text-xs tracking-widest mb-5 text-neutral-500">SHOP</h4>
                            <ul className="space-y-3 text-sm">
                                <li><span onClick={() => navigate('/template17/shop')} className="text-neutral-700 hover:text-neutral-900 cursor-pointer transition-colors">All Products</span></li>
                                <li><span className="text-neutral-700 hover:text-neutral-900 cursor-pointer transition-colors">New Arrivals</span></li>
                                <li><span className="text-neutral-700 hover:text-neutral-900 cursor-pointer transition-colors">Bestsellers</span></li>
                                <li><span className="text-neutral-700 hover:text-neutral-900 cursor-pointer transition-colors">Sale</span></li>
                            </ul>
                        </div>

                        <div>
                            <h4 className="text-xs tracking-widest mb-5 text-neutral-500">HELP</h4>
                            <ul className="space-y-3 text-sm">
                                <li><span onClick={() => navigate('/template17/contact')} className="text-neutral-700 hover:text-neutral-900 cursor-pointer transition-colors">Contact Us</span></li>
                                <li><span onClick={() => navigate('/template17/orders')} className="text-neutral-700 hover:text-neutral-900 cursor-pointer transition-colors">Track Order</span></li>
                                <li><span className="text-neutral-700 hover:text-neutral-900 cursor-pointer transition-colors">Shipping</span></li>
                                <li><span className="text-neutral-700 hover:text-neutral-900 cursor-pointer transition-colors">Size Guide</span></li>
                            </ul>
                        </div>

                        <div>
                            <h4 className="text-xs tracking-widest mb-5 text-neutral-500">CONNECT</h4>
                            <ul className="space-y-3 text-sm">
                                <li className="text-neutral-700">hello@blum.in</li>
                                <li className="text-neutral-700">+91 98765 43210</li>
                                <li className="text-neutral-700 pt-2">Mon - Sat: 10am - 7pm</li>
                            </ul>
                        </div>
                    </div>

                    <div className="border-t border-neutral-200 mt-12 pt-8 flex justify-between items-center gap-4">
                        <p className="text-neutral-500 text-xs">© 2025 Blum. All rights reserved.</p>
                        <div className="flex gap-6 text-xs text-neutral-500">
                            <span className="hover:text-neutral-900 cursor-pointer transition-colors">Privacy Policy</span>
                            <span className="hover:text-neutral-900 cursor-pointer transition-colors">Terms of Service</span>
                        </div>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default Template17;